// @ts-nocheck

// App
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import gmapInit from 'app/client/utils/map/gmapInit';
import CommuteAutocompleteItem from 'app/shared/modules/MobileSRP/Header/FilterModules/CommuteFilter/CommuteAutocompleteItem';

// Actions
import UserPointActions from 'app/shared/flux/actions/UserPointActions';

// Hooks
import { useAutocompleteDropdown } from './hooks';

// Lodash
import debounce from 'lodash/debounce';

// Icons
import IconSearchGrey from 'images/icons/search-v2-gray.min.svg';
import IconSearch from 'images/icons/search-v2.min.svg';
import IconGoogleAttribution from 'images/googleAttribution.png';

// Tracking

// Misc / utils
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import * as S from './styles';

const CommuteAutocomplete = ({ setIsAddressLoaded, handleAddressChange, userPoint, showDropdown, setShowDropdown }) => {
  // Redux
  const dispatch = useDispatch();
  const googleMapsKey = useSelector((state) => state.app.googleMaps.key);
  const userPoints = useSelector((state) => state.user.userPoints.destinations);

  // React
  const autocompleteInputRef = useRef(null);
  const autocompleteService = useRef(null);
  const geocoderService = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [autocompleteRefList, setAutocompleteRefList] = useState([]);
  const [suggestions, setSuggestions] = useState(null);

  // Hooks
  const [autocompleteBasicKeyDownNav, autocompleteKeyDownNav] = useAutocompleteDropdown({
    focusRef: autocompleteInputRef,
    refList: autocompleteRefList,
    showDropdown,
    toggleDropdown: setShowDropdown,
  });

  useEffect(() => {
    if (!autocompleteService.current || !geocoderService.current) {
      gmapInit({ key: googleMapsKey }).then(() => {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        geocoderService.current = new window.google.maps.Geocoder();
      });
    }
  }, []);

  useEffect(() => {
    if (autocompleteRefList.length < 1 && userPoints.length > 0) {
      const refArr = [];
      userPoints.map((_) => {
        const ref = React.createRef(null);
        refArr.push(ref);
      });

      setAutocompleteRefList(refArr);
    }
  }, []);

  const debouncedFetch = useCallback(
    debounce(
      async (query) => {
        autocompleteService.current.getPlacePredictions(
          {
            input: query,
            componentRestrictions: { country: 'us' },
          },
          (predictions) => {
            const refArr = [];
            const newSuggestions = predictions.map((place) => {
              const ref = React.createRef(null);
              refArr.push(ref);
              return {
                description: place.description,
                primaryText: place.structured_formatting.main_text,
                secondaryText: place.structured_formatting.secondary_text,
                placeId: place.place_id,
                ref,
              };
            });
            setAutocompleteRefList(refArr);
            setSuggestions(newSuggestions);
          },
        );
      },
      600,
      { leading: true, trailing: true },
    ),
    [],
  );

  // Input field handlers
  const handleInputChange = (e) => {
    if (!showDropdown) {
      setShowDropdown(true);
    }

    setInputValue(e.target.value);
    debouncedFetch(e.target.value);
  };

  const handleInputKeyDown = (e) => autocompleteBasicKeyDownNav(e.keyCode);

  // Item or user point selection handlers
  const handleSuggestionSelection = (suggestion) => {
    setIsAddressLoaded(false);
    geocoderService.current.geocode({ placeId: suggestion.placeId }, (results) => {
      const location = results[0].geometry.location;
      const newUserPointObj = {
        lat: location.lat(),
        lon: location.lng(),
        name: suggestion.description,
      };

      dispatch(UserPointActions.create(newUserPointObj, { active: true })).then(() => setIsAddressLoaded(true));
      handleAddressChange({ lat: newUserPointObj.lat, lon: newUserPointObj.lon });
      setShowDropdown(false);
    });
  };

  const handlePreviousPointSelection = (point) => {
    dispatch(UserPointActions.select(point.address));
    handleAddressChange({ lat: point.lat, lon: point.lon });
    setShowDropdown(false);
  };

  // Click handlers
  const handleSuggestionItemClick = (suggestion) => handleSuggestionSelection(suggestion);
  const handlePreviousPointClick = (point) => handlePreviousPointSelection(point);

  // Keydown handlers
  const handleSuggestionItemKeyDown = (e, suggestion) => {
    e.stopPropagation();

    const keyCode = e.keyCode;
    const enterKeyPressed = keyCode === VALUE_CONSTANTS.ENTER_KEYCODE;

    if (enterKeyPressed) {
      handleSuggestionSelection(suggestion);
    } else {
      autocompleteKeyDownNav(keyCode);
    }
  };

  const handlePreviousPointKeyDown = (e, point) => {
    e.stopPropagation();

    const keyCode = e.keyCode;
    const enterKeyPressed = keyCode === VALUE_CONSTANTS.ENTER_KEYCODE;

    if (enterKeyPressed) {
      handlePreviousPointSelection(point);
    } else {
      autocompleteKeyDownNav(keyCode);
    }
  };

  const shouldShowSuggestions = suggestions !== null && suggestions.length > 0;
  const shouldShowPreviousUserPoints = suggestions === null && userPoints.length > 0;
  let numberOfItems;

  if (shouldShowSuggestions) {
    numberOfItems = suggestions.length;
  }
  if (shouldShowPreviousUserPoints) {
    numberOfItems = userPoints.length;
  }

  return (
    <S.AutocompleteBarContainer>
      <S.InputIcon
        src={showDropdown ? IconSearch : IconSearchGrey}
        height="16px"
        width="16px"
        aria-hidden="true"
        focusable="false"
        loading="lazy"
      />
      <S.FocusLock disabled={!showDropdown}>
        <S.Input
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          autocapitalize="none"
          autoComplete="off"
          id="commute search box"
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={shouldShowPreviousUserPoints ? userPoint.address : 'Where is your commute?'}
          role="combobox"
          ref={autocompleteInputRef}
          type="text"
          value={inputValue}
        />
        {showDropdown && (shouldShowSuggestions || shouldShowPreviousUserPoints) && (
          <>
            <S.ScreenReaderOnly role="status" aria-live="polite">
              {numberOfItems} {numberOfItems === 1 ? 'result' : 'results'} available. Use the up and down arrows to
              browse and enter to select and go to item. Use the escape key to exit.
            </S.ScreenReaderOnly>
            <S.MenuBox role="listbox" id="listbox">
              <ul role="group" aria-labelledby="suggestion-category">
                {shouldShowSuggestions && (
                  <>
                    <S.SuggestionCategory role="presentation" id="suggestion-category">
                      Suggestions
                    </S.SuggestionCategory>
                    {suggestions.map((suggestion) => {
                      const suggestionRef = suggestion.ref;
                      return (
                        <CommuteAutocompleteItem
                          item={suggestion}
                          handleSelectionClick={handleSuggestionItemClick}
                          handleSelectionKeyDown={handleSuggestionItemKeyDown}
                          customRef={suggestionRef}
                          key={suggestion.placeId}
                        />
                      );
                    })}
                  </>
                )}
                {shouldShowPreviousUserPoints && (
                  <>
                    <S.SuggestionCategory role="presentation" id="suggestion-category">
                      Previous locations
                    </S.SuggestionCategory>
                    {userPoints.map((point, i) => {
                      const pointId = point.id;
                      const pointDetails = point.name.split(',');
                      const formattedItem = {
                        ...point,
                        primaryText: pointDetails[0],
                        secondaryText: pointDetails.slice(1, pointDetails.length).join(','),
                      };
                      const pointRef = autocompleteRefList[i];

                      return (
                        <CommuteAutocompleteItem
                          item={formattedItem}
                          handleSelectionClick={handlePreviousPointClick}
                          handleSelectionKeyDown={handlePreviousPointKeyDown}
                          customRef={pointRef}
                          key={pointId}
                        />
                      );
                    })}
                  </>
                )}
              </ul>
              <img src={IconGoogleAttribution} loading="lazy" />
            </S.MenuBox>
          </>
        )}
      </S.FocusLock>
    </S.AutocompleteBarContainer>
  );
};

export default CommuteAutocomplete;
